import {Button} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {CancelButton} from 'components/DSYS/CancelButton';
import {t} from 'i18next';
import {type ComponentPropsWithoutRef, type ReactNode, useId} from 'react';

type ActionButtonProps = Extract<ComponentPropsWithoutRef<typeof Button>, {href?: never}>;

type WorkstreamModalProps = {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  actionLabel: ReactNode;
  actionProps?: Omit<ActionButtonProps, 'children' | 'variant'>;
  width?: ComponentPropsWithoutRef<typeof Modal>['width'];
};

export const WorkstreamModal = ({
  open,
  title,
  description,
  children,
  onClose,
  actionLabel,
  actionProps,
  width = 'small',
}: WorkstreamModalProps) => {
  const id = useId();

  return (
    <Modal
      open={open}
      withCloseButton={t('close')}
      isCentered
      onRequestClose={onClose}
      width={width}
      aria-labelledby={`${id}-title`}
      aria-describedby={description ? `${id}-description` : undefined}
    >
      <Modal.Header>
        <Modal.Title id={`${id}-title`}>{title}</Modal.Title>
        {description && (
          <Box paddingTop="12">
            <Text id={`${id}-description`} color="subtle">
              {description}
            </Text>
          </Box>
        )}
      </Modal.Header>
      <Modal.Body>{children}</Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
        <Button type="button" {...actionProps} variant="primary">
          {actionLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
